import { Container } from "@mui/material";
import {
  SmartAutoComplete,
  SmartCheckbox,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FerryDetailsDto,
  MapAreaTypeEnum,
  useMapAreaApi,
  useOrganizationApi,
} from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export const ferryDataToForm = (
  f: FerryDetailsDto | undefined,
): FerryFormInputs => {
  return {
    name: f?.name ?? "",
    description: f?.description ?? "",
    active: f?.active ?? true,
    roadNumber: f?.roadNumber ?? "",
    startNumber: f?.startNumber ?? "",
    startDistanceMetre: f?.startDistanceMetre ?? "",
    endNumber: f?.endNumber ?? "",
    endDistanceMetre: f?.endDistanceMetre ?? "",
    lengthMetre: f?.lengthMetre ?? "",
    maintainerOrganizationId: f?.maintainerOrganization?.id ?? null,
    mapAreaIds: f?.mapAreas?.map((m) => m.mapAreaId) ?? [],
  };
};

export interface FerryFormInputs {
  name: string;
  description: string;
  active: boolean;
  roadNumber: number | null | string;
  startNumber: number | null | string;
  startDistanceMetre: number | null | string;
  endNumber: number | null | string;
  endDistanceMetre: number | null | string;
  lengthMetre: number | null | string;
  mapAreaIds: number[] | null;
  maintainerOrganizationId: number | null;
}

interface Props {
  onSubmit: (data: FerryFormInputs) => void;
  readOnly?: boolean;
  small?: boolean;
  defaultValues?: FerryDetailsDto;
  formInitialValues?: FerryFormInputs;
  formId: string;
}

const FerryForm = ({
  onSubmit,
  readOnly,
  small,
  formId,
  formInitialValues,
  defaultValues,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("ferry-page");

  const formDefaultValues = useMemo(
    () => formInitialValues ?? ferryDataToForm(defaultValues),
    [defaultValues, formInitialValues],
  );

  const formMethods = useSmartForm({
    defaultValues: formDefaultValues,
  });
  const { getOrganizations } = useOrganizationApi();
  const { getMapAreas } = useMapAreaApi();

  const { data: organizationData, isLoading: isLoadingOrgs } = useCustomQuery(
    "organizations",
    () => getOrganizations(),
  );
  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => getMapAreas(),
  );
  const orgs = useMemo(() => organizationData?.results, [organizationData]);

  if (isLoadingAreas || isLoadingOrgs) return <LoadingBox />;

  return (
    <Container maxWidth={false}>
      <SmartForm<FerryFormInputs>
        onSubmit={onSubmit}
        gridProps={{ justifyContent: "center" }}
        methods={formMethods}
        formId={formId}
      >
        <SmartTextField<FerryFormInputs>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          required
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<FerryFormInputs>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartCheckbox<FerryFormInputs>
          name="active"
          label={t("active")}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="roadNumber"
          label={t("roadNumber")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="startNumber"
          label={t("startNumber")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="startDistanceMetre"
          label={t("startDistanceMetre")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="endNumber"
          label={t("endNumber")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="endDistanceMetre"
          label={t("endDistanceMetre")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartTextField<FerryFormInputs>
          name="lengthMetre"
          label={t("lengthMetre")}
          type="number"
          gridProps={small ? { xs: 5, lg: 3 } : { xs: 6, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartAutoComplete<FerryFormInputs, number>
          options={orgs?.map((x) => x.id) ?? []}
          fullWidth
          disabled={isLoadingOrgs || readOnly}
          name="maintainerOrganizationId"
          label={t("maintainer-organization")}
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          getOptionLabel={(opt) => {
            const org = orgs?.find((x) => x.id === opt);
            return org?.name ?? t("general:unknown");
          }}
        />
        <SmartAutoComplete<FerryFormInputs, number, true>
          name="mapAreaIds"
          label={t("map-areas")}
          options={mapAreas?.results.map((x) => x.mapAreaId) ?? []}
          gridProps={{ xs: small ? 11 : 12 }}
          fullWidth
          multiple
          disabled={isLoadingAreas || readOnly}
          getOptionLabel={(opt) => {
            if (!(typeof opt === "number")) return "";
            const ma = mapAreas?.results.find((x) => x.mapAreaId === opt);
            if (!ma) return t("general:unknown");
            return ma.mapAreaType === MapAreaTypeEnum.Project
              ? `${ma.projectID ? `${ma.projectID} -` : ""} ${ma.name}`
              : ma.name;
          }}
        />
      </SmartForm>
    </Container>
  );
};

export default FerryForm;
